import { Component, DestroyRef, inject, input, output, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { FileDropComponent } from '@app/shared/components/files/file-drop/file-drop.component';
import { TasksActionsService } from '@app/shared/services/actions/tasks-actions.service';
import { FileDataService } from '@app/shared/services/file-data.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { FileContext } from '@app/shared/types/enums/file-enums';
import { Task } from '@app/shared/types/models/task/task.model';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { ActiveTaskService } from '@app/views/company/projects/tasks/active-task.service';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * @summary Task detail FAB and context menus for mobile FAB and desktop overflow actions.
 * @selector proffeo-task-details-actions-menu
 */
@Component({
  selector: 'proffeo-task-details-actions-menu',
  templateUrl: './task-details-actions-menu.component.html',
  imports: [MatIconModule, MatMenuModule, UiButtonComponent, FileDropComponent, TranslatePipe]
})
export class TaskDetailsActionsMenuComponent {
  private readonly taskActionsService = inject(TasksActionsService);
  private readonly activeTaskService = inject(ActiveTaskService);
  private readonly dialogService = inject(DialogService);
  private readonly fileService = inject(FileDataService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly FileContext = FileContext;

  public readonly task = input.required<Task>();
  public readonly subtaskCreationRequested = output<void>();
  public readonly taskFilesAdded = output<File[]>();
  public readonly taskActionsMenu = viewChild.required<MatMenu>('taskActions');

  protected startSubtaskCreation(): void {
    this.subtaskCreationRequested.emit();
  }

  protected onCopyLink(): void {
    this.taskActionsService.copyTaskLink(this.task());
  }

  protected onCopyTaskNumber(): void {
    this.taskActionsService.copyTaskNumber(this.task());
  }

  protected onCopyTask(): void {
    this.taskActionsService.copyTask(this.task());
  }

  protected openMoveTaskDialog(): void {
    this.taskActionsService.openMoveTaskDialog(this.task());
  }

  protected onGeneratePdf(): void {
    this.dialogService.openGenerateTaskPdfDialog(this.task());
  }

  protected onLockTask(): void {
    this.taskActionsService.lockTask(this.task());
  }

  protected onUnlockTask(): void {
    this.taskActionsService.unlockTask(this.task());
  }

  protected onDeleteTask(): void {
    this.taskActionsService.deleteTask(this.task()).subscribe(() => this.activeTaskService.unsetActiveTask());
  }

  protected onOpenEditFinalCostDialog(): void {
    this.taskActionsService.openEditFinalCostDialog(this.task());
  }

  protected onOpenEditContractCostDialog(): void {
    this.taskActionsService.openEditContractCostDialog(this.task());
  }

  protected openTimeReportDialog(): void {
    this.dialogService
      .openTimeReportDialog(this.task())
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.fileService.reloadTaskFiles(this.task().id);
        }
      });
  }

  protected onFabFilesAdded(files: File[]): void {
    this.taskFilesAdded.emit(files);
  }
}
